<script setup lang="ts">
import type { Swiper as SwiperType } from 'swiper';
import { Autoplay, EffectFade, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { ref } from 'vue';
import OfferBanner from './offer/OfferBanner.vue';
import 'swiper/css';
import 'swiper/css/pagination';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-vue-next';
import Button from '../ui/button/Button.vue';
import { Offer } from '@/types/offer';

interface Props {
  offers?: Offer[];
}

withDefaults(defineProps<Props>(), {
  offers: () => [],
});

const swiper = ref<SwiperType | null>(null);

const next = () => swiper.value?.slideNext();
const prev = () => swiper.value?.slidePrev();

const onSwiper = (instance: SwiperType) => {
  swiper.value = instance;
};
</script>

<template>
  <section id="offers" class="bg-white py-12">
    <div class="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 xl:max-w-7xl">
      <OfferBanner v-if="offers && offers.length === 1" :offer="offers[0]" />

      <Swiper
        v-else
        :modules="[Autoplay, Pagination, EffectFade]"
        :autoplay="{ delay: 5000, disableOnInteraction: false }"
        :fade-effect="{ crossFade: true }"
        :slides-per-view="1"
        :speed="1500"
        :pagination="{ clickable: true }"
        :space-between="10"
        class="h-full rounded-2xl"
        :loop="true"
        @swiper="onSwiper"
      >
        <SwiperSlide v-for="offer in offers" :key="`offer-slide-${offer.id}`" class="h-full">
          <OfferBanner :offer="offer" />
        </SwiperSlide>
      </Swiper>

      <div v-if="offers && offers.length > 1" class="absolute inset-y-0 -left-6 hidden items-center lg:flex">
        <Button size="icon" variant="secondary" @click.prevent="prev" class="offer-swiper-prev cursor-pointer">
          <ChevronLeftIcon class="size-5" />
        </Button>
      </div>

      <div v-if="offers && offers.length > 1" class="absolute inset-y-0 -right-6 hidden items-center lg:flex">
        <Button size="icon" variant="secondary" @click.prevent="next" class="offer-swiper-next cursor-pointer">
          <ChevronRightIcon class="size-5" />
        </Button>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
